import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usersApi }     from '@/api/users';
import { useToast }     from '@/providers/ToastProvider';
import { useAuth }      from '@/providers/AuthProvider';
import {
  Button, FormField, Input, Select, Card, CardContent,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
  Badge, ConfirmDialog, Spinner,
} from '@/components/ui';
import { Plus, Pencil, Trash2, X, Check, KeyRound, Search } from 'lucide-react';
import type { User as UserPublic } from '@/types';

// ─── Schemas ──────────────────────────────────────────────────────────────────
const createSchema = z.object({
  username: z.string().min(3, 'min 3 chars').max(50),
  email:    z.string().email(),
  password: z.string().min(8, 'min 8 chars'),
  role:     z.enum(['admin', 'readonly']),
});

const editSchema = z.object({
  username: z.string().min(3).max(50),
  email:    z.string().email(),
  role:     z.enum(['admin', 'readonly']),
});

const pwdSchema = z.object({
  new_password:     z.string().min(8),
  confirm_password: z.string(),
}).refine(d => d.new_password === d.confirm_password, {
  message: 'Passwords do not match',
  path: ['confirm_password'],
});

type CreateValues = z.infer<typeof createSchema>;
type EditValues   = z.infer<typeof editSchema>;
type PwdValues    = z.infer<typeof pwdSchema>;

type FormMode = 'none' | 'create' | 'edit' | 'password';

export default function UsersPage() {
  const { t }         = useTranslation();
  const queryClient   = useQueryClient();
  const { toast } = useToast();
  const { user: me, isAdmin } = useAuth();

  const [mode, setMode]               = useState<FormMode>('none');
  const [selected, setSelected]       = useState<UserPublic | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserPublic | null>(null);
  const [search, setSearch]           = useState('');

  // ── Query ─────────────────────────────────────────────────────────────────
  const { data: result, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn:  () => usersApi.list({ page: 1, limit: 100 }),
  });
  const users = result?.data ?? [];

  const filteredUsers = users.filter(u =>
    u.username.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  // ── Create form ───────────────────────────────────────────────────────────
  const createForm = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { username: '', email: '', password: '', role: 'readonly' },
  });

  // ── Edit form ─────────────────────────────────────────────────────────────
  const editForm = useForm<EditValues>({
    resolver: zodResolver(editSchema),
    defaultValues: { username: '', email: '', role: 'readonly' },
  });

  // ── Password form ─────────────────────────────────────────────────────────
  const pwdForm = useForm<PwdValues>({
    resolver: zodResolver(pwdSchema),
    defaultValues: { new_password: '', confirm_password: '' },
  });

  const openCreate = () => {
    createForm.reset();
    setSelected(null);
    setMode('create');
  };

  const openEdit = (u: UserPublic) => {
    editForm.reset({ username: u.username, email: u.email, role: u.role });
    setSelected(u);
    setMode('edit');
  };

  const openPassword = (u: UserPublic) => {
    pwdForm.reset();
    setSelected(u);
    setMode('password');
  };

  const closeForm = () => { setMode('none'); setSelected(null); };

  // ── Mutations ─────────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: usersApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast(t('users.created'), 'success');
      closeForm();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: EditValues }) => usersApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast(t('users.updated'), 'success');
      closeForm();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const pwdMutation = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      usersApi.changePassword(id, { new_password: password }),
    onSuccess: () => {
      toast(t('users.passwordChanged'), 'success');
      closeForm();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const deactivateMutation = useMutation({
    mutationFn: usersApi.deactivate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast(t('users.deactivated'), 'success');
      setDeleteTarget(null);
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  if (!isAdmin) {
    return (
      <div className="py-20 text-center text-muted-foreground">
        {t('common.adminOnly')}
      </div>
    );
  }

  return (
    <div>
      {/* ── Sticky top bar ────────────────────────────────────────────── */}
      <div className="sticky top-0 z-10 -mx-4 md:-mx-6 lg:-mx-8 mb-6 bg-background border-b">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-4 md:px-6 lg:px-8 py-4">
          <h1 className="text-2xl font-bold tracking-tight">{t('nav.users')}</h1>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t('common.search')}
                className="pl-9 w-48 sm:w-64"
              />
            </div>
            <Button size="sm" onClick={openCreate} disabled={mode !== 'none'}>
              <Plus size={14} /> {t('users.newUser')}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* ── Side form ───────────────────────────────────────────────────── */}
        {mode !== 'none' && (
          <Card className="lg:col-span-1 order-0 lg:order-1">
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">
                  {mode === 'create'   ? t('users.newUser')
                  : mode === 'edit'   ? t('users.editUser')
                  :                    t('users.changePassword')}
                </h3>
                <Button variant="ghost" size="icon" onClick={closeForm}><X size={14} /></Button>
              </div>

              {mode === 'create' && (
                <form onSubmit={createForm.handleSubmit(d => createMutation.mutate(d))} noValidate className="space-y-3">
                  <FormField label={t('users.username')} error={createForm.formState.errors.username?.message} required>
                    <Input {...createForm.register('username')} autoComplete="off" />
                  </FormField>
                  <FormField label={t('users.email')} error={createForm.formState.errors.email?.message} required>
                    <Input type="email" {...createForm.register('email')} />
                  </FormField>
                  <FormField label={t('users.password')} error={createForm.formState.errors.password?.message} required>
                    <Input type="password" {...createForm.register('password')} autoComplete="new-password" />
                  </FormField>
                  <FormField label={t('users.role')} error={createForm.formState.errors.role?.message} required>
                    <Controller
                      control={createForm.control}
                      name="role"
                      render={({ field }) => (
                        <Select value={field.value} onChange={e => field.onChange(e.target.value)}>
                          <option value="readonly">{t('users.readonly')}</option>
                          <option value="admin">{t('users.admin')}</option>
                        </Select>
                      )}
                    />
                  </FormField>
                  <SaveCancel loading={createMutation.isPending} onCancel={closeForm} />
                </form>
              )}

              {mode === 'edit' && selected && (
                <form onSubmit={editForm.handleSubmit(d => updateMutation.mutate({ id: selected.id, data: d }))} noValidate className="space-y-3">
                  <FormField label={t('users.username')} error={editForm.formState.errors.username?.message} required>
                    <Input {...editForm.register('username')} />
                  </FormField>
                  <FormField label={t('users.email')} error={editForm.formState.errors.email?.message} required>
                    <Input type="email" {...editForm.register('email')} />
                  </FormField>
                  <FormField label={t('users.role')} error={editForm.formState.errors.role?.message} required>
                    <Controller
                      control={editForm.control}
                      name="role"
                      render={({ field }) => (
                        <Select
                          value={field.value}
                          onChange={e => field.onChange(e.target.value)}
                          disabled={selected.id === me?.id}
                        >
                          <option value="readonly">{t('users.readonly')}</option>
                          <option value="admin">{t('users.admin')}</option>
                        </Select>
                      )}
                    />
                  </FormField>
                  {selected.id === me?.id && (
                    <p className="text-xs text-muted-foreground">{t('users.cannotChangeOwnRole')}</p>
                  )}
                  <SaveCancel loading={updateMutation.isPending} onCancel={closeForm} />
                </form>
              )}

              {mode === 'password' && selected && (
                <form onSubmit={pwdForm.handleSubmit(d => pwdMutation.mutate({ id: selected.id, password: d.new_password }))} noValidate className="space-y-3">
                  <p className="text-sm text-muted-foreground">{t('users.changingPasswordFor', { name: selected.username })}</p>
                  <FormField label={t('users.newPassword')} error={pwdForm.formState.errors.new_password?.message} required>
                    <Input type="password" {...pwdForm.register('new_password')} autoComplete="new-password" />
                  </FormField>
                  <FormField label={t('users.confirmPassword')} error={pwdForm.formState.errors.confirm_password?.message} required>
                    <Input type="password" {...pwdForm.register('confirm_password')} autoComplete="new-password" />
                  </FormField>
                  <SaveCancel loading={pwdMutation.isPending} onCancel={closeForm} />
                </form>
              )}
            </CardContent>
          </Card>
        )}

        {/* ── Users table ─────────────────────────────────────────────────── */}
        <div className={mode !== 'none' ? 'lg:col-span-2' : 'lg:col-span-3'}>
          {isLoading ? (
            <Spinner className="py-16" />
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('users.username')}</TableHead>
                    <TableHead className="hidden sm:table-cell">{t('users.email')}</TableHead>
                    <TableHead>{t('users.role')}</TableHead>
                    <TableHead>{t('common.status')}</TableHead>
                    <TableHead className="text-right">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map(u => (
                    <TableRow key={u.id} className={!u.is_active ? 'opacity-50' : undefined}>
                      <TableCell className="font-medium">
                        {u.username}
                        {u.id === me?.id && (
                          <Badge variant="outline" className="ml-2 text-[10px]">You</Badge>
                        )}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-muted-foreground">{u.email}</TableCell>
                      <TableCell>
                        <Badge variant={u.role === 'admin' ? 'default' : 'secondary'}>
                          {u.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={u.is_active ? 'success' : 'destructive'}>
                          {u.is_active ? t('common.active') : t('common.inactive')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" title={t('users.editUser')} onClick={() => openEdit(u)}>
                            <Pencil size={14} />
                          </Button>
                          <Button variant="ghost" size="icon" title={t('users.changePassword')} onClick={() => openPassword(u)}>
                            <KeyRound size={14} />
                          </Button>
                          {u.id !== me?.id && u.is_active && (
                            <Button
                              variant="ghost" size="icon"
                              className="text-destructive hover:text-destructive"
                              title={t('users.deactivate')}
                              onClick={() => setDeleteTarget(u)}
                            >
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('users.deactivateTitle')}
        description={t('users.deactivateDescription', { name: deleteTarget?.username })}
        onConfirm={() => deleteTarget && deactivateMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
        loading={deactivateMutation.isPending}
        confirmLabel={t('users.deactivate')}
      />
    </div>
  );
}

function SaveCancel({ loading, onCancel }: { loading: boolean; onCancel: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex gap-2 pt-2">
      <Button type="submit" disabled={loading} className="flex-1">
        <Check size={14} /> {loading ? t('common.saving') : t('common.save')}
      </Button>
      <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
        {t('common.cancel')}
      </Button>
    </div>
  );
}
